/**
 * Keeps every tab honest about *who* it is signed in as (VTK-1918 follow-up).
 *
 * The credential is a cookie, which the browser scopes per ORIGIN — every tab shares one. The
 * client's picture of the user (name, role, permissions) is persisted to `sessionStorage`, which is
 * scoped per TAB. Signing in as someone else in a second tab therefore overwrites the shared cookie
 * while every other tab keeps rendering the previous identity: the screen says one user, the server
 * acts as another. Measured directly — a tab showing a client admin kept its client-admin UI while a
 * super-admin-only endpoint went from 403 to 200 for that same tab.
 *
 * That is not a cosmetic mismatch. On a product that records who attested to a Form I-9, work
 * silently attributed to the wrong person is the actual damage.
 *
 * Two logins cannot both be live in one browser — one cookie, one identity — so this does not try to
 * keep them apart. It makes the collision LOUD: the tab left holding a stale identity signs itself
 * out instead of continuing to act as someone it is not.
 *
 * `localStorage` is the channel because a `storage` event fires only in the OTHER tabs, never the
 * one that wrote it, which is exactly the audience that needs telling. It carries an opaque user id
 * — no name, email, role or token.
 */

const IDENTITY_KEY = 'authIdentity';

/**
 * Tell the other tabs who this browser is now signed in as. `null` on sign-out.
 *
 * Written on every completed sign-in, so a tab that was already open learns it has been displaced.
 */
export function announceIdentity(appUserId: string | null): void {
  try {
    localStorage.setItem(IDENTITY_KEY, appUserId ?? '');
  } catch {
    // Storage can throw (private mode, quota). This is a safety net, not a gate: failing to
    // announce leaves today's behaviour, so it must never break the sign-in it decorates.
  }
}

/**
 * Watch for another tab taking the browser's single session, and report it when the new identity is
 * not the one this tab believes it is.
 *
 * The announced identity is handed to the subscriber because the two ways this tab loses its session
 * are not the same event, and telling someone who signed out elsewhere that they signed IN elsewhere
 * is wrong in the one moment they are trying to work out what just happened. `null` means the browser
 * was signed out; a user id means somebody else took the cookie.
 *
 * Returns an unsubscribe function. Only live changes are acted on — the value already in storage at
 * load is ignored, so a leftover from a previous browser session cannot sign anyone out.
 */
export function subscribeToIdentityChange(
  currentAppUserId: string | null | undefined,
  onDisplaced: (announcedAppUserId: string | null) => void
): () => void {
  const handler = (event: StorageEvent) => {
    if (event.key !== IDENTITY_KEY) return;
    // A tab with no identity of its own has nothing to be displaced from — notably the public
    // candidate screens, which are not signed in at all and must never be redirected to a login.
    if (!currentAppUserId) return;
    const announced = event.newValue ?? '';
    if (announced === currentAppUserId) return;
    onDisplaced(announced || null);
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
